// REJJI. Gourde cadeau : celle de l'abonnement, et celle du code DECOUVERTE.
//
// 16/10/2026 (décision de Clément, modèle Nuoro, sans code) : la gourde est le cadeau de
// l'abonnement, livrée avec la 1re boîte, pour tout abonné. La réduction automatique
// « Gourde offerte avec la 1re livraison d'abonnement » la met à 0 €.
//
// Ce que fait ce script, et rien d'autre :
//   - une boîte en abonnement au panier, OU code DECOUVERTE + au moins 2 boîtes (promesse
//     tenue jusqu'au 30/11), et pas encore de gourde-offre
//     → ajoute UNE gourde avec la propriété de ligne `_offre` (`decouverte` si le code est
//     présent, sinon `abonnement`) ;
//   - plus d'abonnement au panier, et plus de droit par le code → retire cette gourde-là ;
//     une gourde n'est jamais retirée d'un panier qui contient une boîte en abonnement ;
//   - quantité de la gourde-offre poussée au-dessus de 1, ou ligne scindée par Shopify
//     (1 unité exonérée sur 3) → une seule gourde-offre, à 1.
//
// C'est la fonction Shopify « Offre découverte REJJI » qui met cette gourde à 0 €, en
// reconnaissant la propriété `_offre`. Une gourde ajoutée à la main par le client n'a
// pas cette propriété et reste à son prix. Le tiret bas fait que Shopify n'affiche pas
// la propriété au panier ni au paiement.
//
// Créé le 08/09/2026. Chargé par snippets/scripts.liquid.

import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

const CODE = 'DECOUVERTE';
const ESSAI_KEY = 'rejji-decouverte-essai-gourde';
const SOURCE = 'offre-decouverte';
const BOITE_PRODUCT_ID = 10178713780566;
const GOURDE_PRODUCT_ID = 10179240395094;
const GOURDE_VARIANT_ID = 52553691595094;
const ATTR_KEY = '_offre';
const ATTR_VALUE = 'decouverte';
const ATTR_VALUE_ABONNEMENT = 'abonnement';

let enCours = false;

function sectionsDrawer() {
  const drawer = document.querySelector('cart-drawer');
  return drawer?.dataset?.sectionId ? drawer.dataset.sectionId : '';
}

async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

function rafraichir(data) {
  if (window.location.pathname.includes('/cart')) {
    window.location.reload();
    return;
  }
  document.dispatchEvent(new CartUpdatedEvent({}, { source: SOURCE, sections: data?.sections }));
}

async function synchroniser() {
  if (enCours) return;
  enCours = true;
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

    const codes = (cart.discount_codes || []).filter(
      (d) => typeof d.code === 'string' && d.code.toUpperCase() === CODE,
    );
    const codePresent = codes.length > 0;
    const codeActif = codes.some((d) => d.applicable);
    // 14/10/2026 : avec 2 ou 3 boîtes, la remise automatique « Pack » donne le même pourcentage
    // que DECOUVERTE, et Shopify la retient tant que la gourde n'est pas au panier : le code reste
    // « non applicable ». On ajoute donc la gourde dès que le code est présent ; Shopify bascule
    // alors sur DECOUVERTE (gourde à 0 €, vérifié en ligne le 14/10). Si le code reste refusé après
    // cet essai (déjà utilisé, par exemple), on retire la gourde et on ne réessaie pas : pas de boucle.
    let essai = false;
    try { essai = sessionStorage.getItem(ESSAI_KEY) === '1'; } catch (e) { /* stockage bloqué */ }
    if (codeActif || !codePresent) {
      try { sessionStorage.removeItem(ESSAI_KEY); } catch (e) { /* stockage bloqué */ }
      essai = false;
    }
    const boites = cart.items.filter((i) => i.product_id === BOITE_PRODUCT_ID);
    // Abonnement : la gourde est due, avec ou sans code (16/10/2026).
    const aboPresent = boites.some((i) => !!i.selling_plan_allocation);
    // Sans abonnement : seulement avec DECOUVERTE et au moins 2 boîtes (règle du 05/10/2026).
    const droitParCode = boites.reduce((n, i) => n + i.quantity, 0) >= 2;
    // Toutes les lignes gourde-offre. Il peut y en avoir plusieurs : quand la fonction
    // n'exonère qu'une unité sur une ligne de 3, Shopify scinde la ligne en deux clés.
    const gourdesOffre = cart.items.filter(
      (i) =>
        i.product_id === GOURDE_PRODUCT_ID &&
        i.properties &&
        (i.properties[ATTR_KEY] === ATTR_VALUE || i.properties[ATTR_KEY] === ATTR_VALUE_ABONNEMENT),
    );

    const ajoutParCode = codePresent && droitParCode && (codeActif || !essai);
    if (gourdesOffre.length === 0 && (aboPresent || ajoutParCode)) {
      if (!aboPresent && !codeActif) {
        try { sessionStorage.setItem(ESSAI_KEY, '1'); } catch (e) { /* stockage bloqué */ }
      }
      const valeur = codePresent ? ATTR_VALUE : ATTR_VALUE_ABONNEMENT;
      const data = await post(Theme.routes.cart_add_url, {
        items: [{ id: GOURDE_VARIANT_ID, quantity: 1, properties: { [ATTR_KEY]: valeur } }],
        sections: sectionsDrawer(),
      });
      rafraichir(data);
      return;
    }

    if (gourdesOffre.length > 0 && !aboPresent && (!codeActif || !droitParCode)) {
      if (codePresent && !codeActif) {
        try { sessionStorage.setItem(ESSAI_KEY, '1'); } catch (e) { /* stockage bloqué */ }
      }
      const updates = {};
      for (const g of gourdesOffre) updates[g.key] = 0;
      const data = await post(Theme.routes.cart_update_url, { updates, sections: sectionsDrawer() });
      rafraichir(data);
      return;
    }

    const quantiteTotale = gourdesOffre.reduce((n, g) => n + g.quantity, 0);
    if (gourdesOffre.length > 1 || quantiteTotale > 1) {
      // On garde la première à 1, on retire les autres, en une seule requête.
      const updates = {};
      gourdesOffre.forEach((g, index) => { updates[g.key] = index === 0 ? 1 : 0; });
      const data = await post(Theme.routes.cart_update_url, { updates, sections: sectionsDrawer() });
      rafraichir(data);
    }
  } catch (error) {
    console.error('Offre découverte : synchronisation de la gourde impossible', error);
  } finally {
    enCours = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdated, (event) => {
  if (event?.detail?.data?.source === SOURCE) return;
  synchroniser();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', synchroniser, { once: true });
} else {
  synchroniser();
}
